/**
 * A source permission implies these targets only when the source screen cannot
 * finish a required step without them.
 *
 * Coupon and wallet-coupon add/update include a required customer picker when
 * the coupon is limited to specific customers. That list is Customers → View.
 * Customer add, update, and delete stay off.
 *
 * Item add/update needs the menu and category lists (Menus → View, including
 * Full Access so the category and menu screens open) and the country list.
 * Modifier, combo, and country edit stay off.
 */
const ITEM_FORM_VIEWS = [
  {
    categoryId: 4,
    permissionId: "menus_1",
    subPermissionIds: ["menu_full_access_view"],
  },
  { categoryId: 11, permissionId: "country_1" },
]

export const PERMISSION_DEPENDENCIES = [
  {
    source: { categoryId: 14, permissionId: "promotion_2" },
    requires: [{ categoryId: 7, permissionId: "customersPermission_1" }],
  },
  {
    source: { categoryId: 14, permissionId: "promotion_3" },
    requires: [{ categoryId: 7, permissionId: "customersPermission_1" }],
  },
  {
    source: { categoryId: 46, permissionId: "coupon_redeem_2" },
    requires: [{ categoryId: 7, permissionId: "customersPermission_1" }],
  },
  {
    source: { categoryId: 46, permissionId: "coupon_redeem_3" },
    requires: [{ categoryId: 7, permissionId: "customersPermission_1" }],
  },
  {
    source: { categoryId: 4, permissionId: "menus_2" },
    requires: ITEM_FORM_VIEWS,
  },
  {
    source: { categoryId: 4, permissionId: "menus_3" },
    requires: ITEM_FORM_VIEWS,
  },
]

const isNodeEnabled = permission =>
  Boolean(permission && (permission.isEnabled || permission.enable))

const findPermission = (groups, categoryId, permissionId) => {
  const group = (groups || []).find(
    entry => Number(entry?.id) === Number(categoryId)
  )
  if (!group || !Array.isArray(group.permissions)) return null
  return (
    group.permissions.find(perm => String(perm?.id) === String(permissionId)) ||
    null
  )
}

export const applyPermissionDependencies = (permissionGroups = []) => {
  const groups = JSON.parse(JSON.stringify(permissionGroups || []))

  PERMISSION_DEPENDENCIES.forEach(rule => {
    const source = findPermission(
      groups,
      rule.source.categoryId,
      rule.source.permissionId
    )
    if (!isNodeEnabled(source)) return

    rule.requires.forEach(req => {
      const group = groups.find(
        entry => Number(entry?.id) === Number(req.categoryId)
      )
      if (!group || !Array.isArray(group.permissions)) return
      const perm = group.permissions.find(
        entry => String(entry?.id) === String(req.permissionId)
      )
      if (!perm) return
      perm.isEnabled = true
      perm.enable = true
      ;(req.subPermissionIds || []).forEach(subId => {
        const sub = (perm.subPermissions || []).find(
          entry => String(entry?.id) === String(subId)
        )
        if (!sub) return
        sub.isEnabled = true
        sub.enable = true
      })
    })
  })

  return groups
}

export const isSubPermissionImplied = (
  permissionGroups,
  categoryId,
  permissionId,
  subPermissionId
) =>
  PERMISSION_DEPENDENCIES.some(rule => {
    const required = rule.requires.some(
      req =>
        Number(req.categoryId) === Number(categoryId) &&
        String(req.permissionId) === String(permissionId) &&
        (req.subPermissionIds || []).some(
          subId => String(subId) === String(subPermissionId)
        )
    )
    if (!required) return false
    return isNodeEnabled(
      findPermission(
        permissionGroups,
        rule.source.categoryId,
        rule.source.permissionId
      )
    )
  })

export const isPermissionImplied = (permissionGroups, categoryId, permissionId) =>
  PERMISSION_DEPENDENCIES.some(rule => {
    const required = rule.requires.some(
      req =>
        Number(req.categoryId) === Number(categoryId) &&
        String(req.permissionId) === String(permissionId)
    )
    if (!required) return false
    return isNodeEnabled(
      findPermission(
        permissionGroups,
        rule.source.categoryId,
        rule.source.permissionId
      )
    )
  })
